import { useMutation } from 'react-query';
import { axios } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { showNotification } from '@/utils/notificationUtils';
import { LineItem, MultiAppointmentResponse } from '../types';
export type BookAppointmentDTO = {
  id: number;
  amount: number;
  otherCharges: number;
  storeDiscount: number;
  serviceTip?: number;
  chargeNotes?: string;
  isRefund: boolean;
  roomId?: string;
  paymentType?: string;
  paymentProvider?: string;
  verificationToken?: string;
  cardId?: string;
  tax?: number;
  cardDetails?: {
    paymentMethodId: string;
  };
};

export const requestPayment = ({
  id,
  amount,
  otherCharges,
  storeDiscount,
  serviceTip,
  chargeNotes,
  isRefund,
  roomId,
  paymentProvider,
  paymentType,
  cardId,
  cardDetails,
  verificationToken,
  tax
}: BookAppointmentDTO): Promise<BookAppointmentDTO> => {
  if (!roomId) {
    return axios.post(isRefund ? `/payment/refund-extra/${id}` : `/payment/request/${id}`, {
      amount,
      otherCharges,
      storeDiscount,
      chargeNotes,
      serviceTip,
      paymentType,
      paymentProvider,
      cardId,
      cardDetails,
      verificationToken,
      tax
    });
  } else {
    return axios.post(
      isRefund
        ? `rooms/${roomId}/payment/refund-extra/${id}`
        : `rooms/${roomId}/payment/request/${id}`,
      {
        amount,
        otherCharges,
        chargeNotes,
        storeDiscount,
        paymentType,
        paymentProvider,
        cardId,
        cardDetails,
        verificationToken,
        tax
      }
    );
  }
};

type UseUpdateBusinessOptions = {
  config?: MutationConfig<typeof requestPayment>;
};

export const useRequestPayment = ({ config }: UseUpdateBusinessOptions = {}) => {
  return useMutation({
    onSuccess: (response, data) => {
      if (data.paymentType) {
        showNotification({
          type: 'success',
          title: 'Payment captured successfully',
        });
      } else {
        if (data.isRefund) {
          showNotification({
            type: 'success',
            title: 'Refund request sent successfully',
          });
        }
      }
    },
    ...config,
    mutationFn: requestPayment,
  });
};

export type AppointmentExtraDTO = {
  id: number;
  otherCharges: number;
  chargeNotes?: string;
  storeDiscount: number;
  roomId?: string;
  serviceTip: number;
  tax?: number;
};

export const handleAppointmentExtras = ({
  id,
  otherCharges,
  chargeNotes,
  storeDiscount,
  roomId,
  serviceTip,
  tax
}: AppointmentExtraDTO): Promise<LineItem> => {
  return axios.post(
    roomId ? `/rooms/${roomId}/bookings/update-extra/${id}` : `/payment/update-extra/${id}`,
    { otherCharges, storeDiscount, serviceTip, chargeNotes, tax }
  );
};

type UseUpdateAppoinmentOptions = {
  config?: MutationConfig<typeof handleAppointmentExtras>;
};

export const useUpdateAppoinment = ({ config }: UseUpdateAppoinmentOptions = {}) => {
  return useMutation({
    onSuccess: () => {
      showNotification({
        type: 'success',
        title: 'Appointment updated successfully',
      });
    },
    ...config,
    mutationFn: handleAppointmentExtras,
  });
};

export type MultiAppointmentPaymentDTO = {
  ids: string[];
};

export const handleMultiAppointmentPayment = ({
  ids,
}: MultiAppointmentPaymentDTO): Promise<MultiAppointmentResponse> => {
  return axios.post(`/payment/multi/details`, { ids });
};

type UseMultiAppointmentOptions = {
  config?: MutationConfig<typeof handleMultiAppointmentPayment>;
};

export const useMultiAppoinmentPayment = ({ config }: UseMultiAppointmentOptions = {}) => {
  return useMutation({
    ...config,
    mutationFn: handleMultiAppointmentPayment,
  });
};

export type MultiAppointmentPaymentRequestDTO = {
  appointmentIds: string[];
  amount?: number;
  otherCharges?: number;
  chargeNotes?: string;
  storeDiscount?: number;
  serviceTip?: number;
  paymentType?: string;
  paymentProvider?: string;
  verificationToken?: string;
  cardId?: string;
  tax?: number;
  cardDetails?: {
    paymentMethodId: string;
  };
};

export const handleMultiAppointmentPaymentRequest = ({
  appointmentIds,
  amount,
  otherCharges,
  storeDiscount,
  serviceTip,
  paymentType,
  paymentProvider,
  verificationToken,
  cardDetails,
  cardId,
  tax,
}: MultiAppointmentPaymentRequestDTO): Promise<MultiAppointmentResponse> => {
  return axios.post(`/payment/multi/request`, {
    appointmentIds,
    amount,
    otherCharges,
    storeDiscount,
    serviceTip,
    paymentType,
    paymentProvider,
    verificationToken,
    cardDetails,
    cardId,
    tax,
  });
};

type UseMultiAppointmentRequestOptions = {
  config?: MutationConfig<typeof handleMultiAppointmentPaymentRequest>;
};

export const useMultiAppoinmentPaymentRequest = ({
  config,
}: UseMultiAppointmentRequestOptions = {}) => {
  return useMutation({
    onSuccess: (response, data) => {
      if (data.paymentType) {
        showNotification({
          type: 'success',
          title: 'Payment captured successfully',
        });
      }
    },
    ...config,
    mutationFn: handleMultiAppointmentPaymentRequest,
  });
};

export type FeedbackDTO = {
  email: string;
  phone: string;
  id: number;
  roomId?: string;
};

export const feedbackPayment = ({
  email,
  phone,
  id,
  roomId,
}: FeedbackDTO): Promise<FeedbackDTO> => {
  return axios.post(
    roomId
      ? `/rooms/${roomId}/booking/${id}/review/request`
      : `/business/appointment/${id}/review/request`,
    { email, phone }
  );
};

type UseUpdateFeedbackOptions = {
  config?: MutationConfig<typeof feedbackPayment>;
};

export const useRequestFeedback = ({ config }: UseUpdateFeedbackOptions = {}) => {
  return useMutation({
    onSuccess: () => {
      showNotification({
        type: 'success',
        title: 'Feedback request sent successfully',
      });
    },
    ...config,
    mutationFn: feedbackPayment,
  });
};
